import { useEffect, useMemo, useState } from "react";
import type { ISearchResponse, ICatalogSearchParams } from "../types/catalog";

const useCatalogSearch = (params: ICatalogSearchParams) => {
    const { q = "", types = [], visibility, page = 0, limit = 20, currentUser = null } = params;

    const effectiveLimit = Math.max(1, limit) + 1;

    const queryString = useMemo(() => {
        const safeLimit = Math.max(1, limit);
        const safePage = Math.max(0, page);
        const skip = safePage * safeLimit;

        const qs = new URLSearchParams();
        if (q.trim()) qs.set("q", q.trim());
        if (types.length) qs.set("types", types.join(","));
        if (visibility) qs.set("visibility", visibility);
        qs.set("limit", String(effectiveLimit));
        qs.set("skip", String(skip));
        if (currentUser) qs.set("username", currentUser);

        return qs.toString();
    }, [q, types, visibility, page, limit, currentUser, effectiveLimit]);

    const [data, setData] = useState<ISearchResponse | null>(null);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        const run = async () => {
            setLoading(true);
            setError(null);
            setHasMore(false);
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/catalogs/search?${queryString}`, {
                    signal: controller.signal,
                });
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(`Search failed (${res.status}): ${text || res.statusText}`);
                }

                const json = (await res.json()) as ISearchResponse;

                const results = Array.isArray((json as any).results) ? (json as any).results : [];
                const more = results.length > Math.max(1, limit);
                setHasMore(more);

                const trimmed = more ? results.slice(0, Math.max(1, limit)) : results;

                setData({ ...(json as any), results: trimmed } as ISearchResponse);
            } catch (err: any) {
                if (err?.name !== "AbortError") setError(err);
            } finally {
                setLoading(false);
            }
        };
        run();
        return () => controller.abort();
    }, [queryString, limit]);

    return { data, hasMore, loading, error };
};

export default useCatalogSearch;
